import type { LucideIcon } from 'lucide-react';
import {
  Activity,
  BookOpen,
  Bot,
  CalendarCheck2,
  CalendarClock,
  CircleDot,
  FileText,
  Lightbulb,
  List,
  Sparkles,
  TrendingUp,
  User,
  UserCheck,
  UserX,
  Users,
  Waypoints,
} from 'lucide-react';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '@/lib/utils';

const kpiVariants = cva(
  [
    'inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-[10px] border px-3 py-1 text-xs',
    'border-border/50 bg-card/50 shadow-xs backdrop-blur-sm',
    'dark:border-[rgba(55,65,81,0.5)] dark:bg-[rgba(30,30,58,0.4)]',
    'dark:shadow-[0px_8px_32px_rgba(124,58,237,0.2),0px_2px_8px_rgba(0,0,0,0.3)]',
  ].join(' '),
  {
    variants: {
      variant: {
        copilots: '',
        upcoming: '',
        completed: '',
        participants: '',
        priority_high: '',
        priority_medium: '',
        priority_low: '',
        reports: '',
        in_progress: '',
        pipeline: '',
        interviews_total: '',
        interviews_signals: '',
        confidence: '',
        snapshot_interviews: '',
        snapshot_research: '',
        snapshot_recommendations: '',
        insight: '',
      },
    },
    defaultVariants: {
      variant: 'copilots',
    },
  },
);

type KpiVariant = NonNullable<VariantProps<typeof kpiVariants>['variant']>;

const variantIcons: Record<KpiVariant, LucideIcon> = {
  copilots: Bot,
  upcoming: CalendarClock,
  completed: CalendarCheck2,
  participants: Users,
  priority_high: UserCheck,
  priority_medium: User,
  priority_low: UserX,
  reports: FileText,
  in_progress: CircleDot,
  pipeline: Waypoints,
  interviews_total: List,
  interviews_signals: Activity,
  confidence: TrendingUp,
  snapshot_interviews: Users,
  snapshot_research: BookOpen,
  snapshot_recommendations: Lightbulb,
  insight: Sparkles,
};

const variantIconColors: Record<KpiVariant, string> = {
  copilots: 'text-purple-400',
  upcoming: 'text-blue-400',
  completed: 'text-emerald-400',
  participants: 'text-violet-400',
  priority_high: 'text-emerald-400',
  priority_medium: 'text-orange-400',
  priority_low: 'text-red-400',
  reports: 'text-violet-400',
  in_progress: 'text-orange-400',
  pipeline: 'text-cyan-400',
  interviews_total: 'text-blue-400',
  interviews_signals: 'text-purple-400',
  confidence: 'text-blue-400',
  snapshot_interviews: 'text-purple-400',
  snapshot_research: 'text-amber-400',
  snapshot_recommendations: 'text-emerald-400',
  insight: 'text-emerald-400',
};

const defaultLabels: Partial<Record<KpiVariant, string>> = {
  copilots: 'Copilots',
  upcoming: 'Upcoming',
  completed: 'Completed',
  participants: 'Participants',
  priority_high: 'High Fit',
  priority_medium: 'Medium',
  priority_low: 'Low',
  reports: 'Reports',
  in_progress: 'In Progress',
  pipeline: 'Pipeline',
  interviews_total: 'Total',
  interviews_signals: 'Signals',
  confidence: 'Confidence',
  snapshot_interviews: 'Interviews',
  snapshot_research: 'Research',
  snapshot_recommendations: 'Recommendations',
};

export interface KpiCardProps extends VariantProps<typeof kpiVariants> {
  value: number | string;
  label?: string;
  icon?: LucideIcon;
  /** When set, overrides the variant’s default icon color (e.g. insight confidence tiers). */
  iconClassName?: string;
  className?: string;
}

export function KpiCard({
  variant = 'copilots',
  value,
  label,
  icon,
  iconClassName,
  className,
}: KpiCardProps) {
  const resolvedVariant = variant ?? 'copilots';
  const Icon = icon ?? variantIcons[resolvedVariant];
  const iconColor = iconClassName ?? variantIconColors[resolvedVariant];
  const displayLabel = label ?? defaultLabels[resolvedVariant];

  return (
    <div
      className={cn(kpiVariants({ variant }), className)}
      role="group"
      aria-label={displayLabel ? `${displayLabel}: ${value}` : String(value)}
    >
      <Icon className={cn('size-[18px] shrink-0', iconColor)} aria-hidden />
      <span className="font-bold tabular-nums leading-[21px] text-foreground">{value}</span>
      {displayLabel ? (
        <span
          className="font-light leading-[18px] text-muted-foreground dark:text-[color:var(--color-primary-50)]"
          style={{ fontSize: 12 }}
        >
          {displayLabel}
        </span>
      ) : null}
    </div>
  );
}
